import React from 'react';
import shallowCompare from 'is-equal-shallow';
import { filter, noop, get } from 'lodash';
import { connect as libConnect } from 'react-redux';

import { PropTypes } from 'helpers/react';
const { func } = PropTypes;

import LoadingScreen from 'shared-components/LoadingScreen';

/**
 * withData
 *
 * @param {array} dataFetchActions - an array of objects, each specifying a data
 * dependency.
 * Valid options for the objects are:
 * `action` (required): a function accepting the component's ownProps and returning
 * an action to be dispatched. ie:
 *
 * @withData([{
 *     action: (props) => fetchUser(props.id)
 *   }]
 *
 * Actions will be dispatched immediately on UNSAFE_componentWillMount, and again
 * whenever the component's props change, except as configured in the other
 * options below
 *
 * `blockPageLoad`: a boolean specifying that the view should wait on this action
 * to settle (succeed or fail) before rendering. withData will render a
 * <LoadingScreen> until the promise returned by dispatching the action settles.
 * blockPageLoad actions are *only* dispatched on UNSAFE_componentWillMount.
 *
 * `watchProps`: an array of prop names that, when changed (compared with ===),
 * should cause the action to be dispatched again. ie:
 *
 *  @withData([{
 *    action: (props) => fetchUser(props.id, props.options),
 *    // re-fetch if props.id changes
 *    watchProps: ['id']
 *  }]
 *
 *  This prevents unnecessary calls when non-watched props change
 *
 *
 * @param {function} connect - connect() from react-redux (provided by default),
 * or a stub function for tests
 */
const withData = (
  dataFetchActions = [],
  connect = libConnect
) => WrappedComponent => {
  // provides props.dispatch
  @connect()
  class WrappedWithData extends React.Component {
    static propTypes = {
      dispatch: func.isRequired
    };

    static defaultProps = {};

    state = {
      loading: false
    };

    UNSAFE_componentWillMount() {
      this.dispatchDataFetchActions(this.props);
    }

    UNSAFE_componentWillUpdate(nextProps) {
      // ignore state
      if (!shallowCompare(this.props, nextProps)) {
        // ignore actions that specify watchedProps and don't care about this change
        const actionsWithWatchers = filter(dataFetchActions, 'watchProps');
        const excludeActions = actionsWithWatchers.filter(({ watchProps }) => {
          // actions that had watched props that didn't change
          return watchProps.every(
            path => get(this.props, path) === get(nextProps, path)
          );
        });

        this.dispatchDataFetchActions(nextProps, excludeActions, false);
      }
    }

    dispatchDataFetchActions(
      props,
      excludeActions = [],
      dispatchBlocking = true
    ) {
      const blockingActions = filter(dataFetchActions, 'blockPageLoad');

      // only dispatch blocking actions on mount
      if (dispatchBlocking && blockingActions.length) {
        this.setLoading();

        Promise.all(
          blockingActions.map(({ action }) => props.dispatch(action(props)))
        )
          .catch(noop)
          .then(() => this.setLoading(false));
      }

      filter(dataFetchActions, ({ blockPageLoad }) => !blockPageLoad)
        .filter(action => !excludeActions.includes(action))
        .map(({ action }) => props.dispatch(action(props)));
    }

    setLoading(state = true) {
      this.setState({ loading: state });
    }

    render() {
      return this.state.loading
        ? <LoadingScreen />
        : <WrappedComponent {...this.props} />;
    }
  }

  return WrappedWithData;
};

export default withData;



// WEBPACK FOOTER //
// ./src/client/shared-components/withData/index.js